import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, CircularProgress, Typography } from '@mui/material';
import { circularProgressSizeProps } from '@opengov/capital-mui-theme';
import React, { useRef } from 'react';

import { styles } from './PreviewContainer.styles';
import { usePdfPreviewNavigationGuard } from './usePdfPreviewNavigationGuard';

export interface IPreviewContainerProps {
  previewSrc: string;
  showInitialSpinner: boolean;
  showSpinner: boolean;
  showCompleted: boolean;
  completedLabel: string;
  spinnerLabel: string;
  previewTitle: string;
  /** When set, no preview is shown; this explains why in its place. */
  unavailableMessage?: string;
}

export const PreviewContainer: React.FC<IPreviewContainerProps> = ({
  previewSrc,
  spinnerLabel,
  showCompleted,
  completedLabel,
  showInitialSpinner,
  showSpinner,
  previewTitle,
  unavailableMessage
}) => {
  const statusSpinnerId = 'status-spinner-label';
  const initialSpinnerId = 'initial-spinner-label';
  const iframeWrapperRef = useRef<HTMLElement>(null);
  const isPreviewAvailable = !unavailableMessage;
  const showInitialLoader = isPreviewAvailable && showInitialSpinner && !showSpinner;
  const showPreviewFrame = isPreviewAvailable && Boolean(previewSrc);

  usePdfPreviewNavigationGuard(iframeWrapperRef);

  return (
    <Box sx={styles.previewContainer}>
      <Box sx={styles.header}>
        <Typography sx={styles.headerTitle}>{previewTitle}</Typography>
        {isPreviewAvailable && showSpinner && (
          <Box sx={styles.status} data-test="pdf-preview-loader">
            <CircularProgress
              {...circularProgressSizeProps.small}
              aria-labelledby={statusSpinnerId}
              aria-label={spinnerLabel}
              role="progressbar"
            />
            <Typography id={statusSpinnerId} sx={styles.label}>
              {spinnerLabel}
            </Typography>
          </Box>
        )}
        {isPreviewAvailable && showCompleted && (
          <Box sx={styles.status}>
            <CheckCircleIcon color="success" aria-hidden="true" />
            <Typography sx={styles.label}>{completedLabel}</Typography>
          </Box>
        )}
      </Box>
      <Box sx={styles.iframeContainer} ref={iframeWrapperRef}>
        {unavailableMessage && (
          <Box sx={styles.spinnerContainer} role="alert" data-test="pdf-preview-unavailable">
            <Typography>{unavailableMessage}</Typography>
          </Box>
        )}

        {showInitialLoader && (
          <Box sx={styles.spinnerContainer} data-test="report-exporting-pdf">
            <CircularProgress
              {...circularProgressSizeProps.large}
              aria-labelledby={initialSpinnerId}
              aria-label="Loading"
              role="progressbar"
            />
            <Typography id={initialSpinnerId}>Loading</Typography>
          </Box>
        )}

        {showPreviewFrame && (
          <iframe data-test="preview-iframe" title="Export PDF Preview" width="100%" height="100%" src={previewSrc} />
        )}
      </Box>
    </Box>
  );
};
